import purchaseinvoiceService from '../../../services/purchaseinvoiceService';
import filterService from '../../../services/filterService';

import {
    setPurchaseInvoiceLoading,
    setPurchaseInvoiceError,
    setPurchaseInvoiceList,
    setPurchaseInvoiceCurrent,
    setPurchaseInvoicePagination,
    setPurchaseInvoiceSort,
    setPurchaseInvoiceFilters,
    setPurchaseInvoiceDefaultFilter,
    setPurchaseInvoiceSettings,
    clearPurchaseInvoiceCurrent,
    clearPurchaseInvoiceError,
    setPurchaseInvoiceFilterLoading,
    changePurchaseInvoiceFilterCondition,
    addPurchaseInvoiceFilterCondition,
    updatePurchaseInvoiceFilterConditionRule,
    updatePurchaseInvoiceFilterConditionValue,
    removePurchaseInvoiceFilterCondition,
    setPurchaseInvoiceSelected,
    resetPurchaseInvoiceFiltersAndPagination
} from './purchaseinvoiceSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().purchaseinvoice.isSettingsInitialized) {
            dispatch(setPurchaseInvoiceLoading(true));
            const response = await purchaseinvoiceService.getSettings();

            // Set the settings
            dispatch(setPurchaseInvoiceSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setPurchaseInvoiceDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setPurchaseInvoiceError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setPurchaseInvoiceLoading(false));
    }
};

export const fetchPurchaseinvoices = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().purchaseinvoice;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setPurchaseInvoiceLoading(true));
        const response = await purchaseinvoiceService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setPurchaseInvoiceList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchPurchaseinvoices:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setPurchaseInvoiceError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setPurchaseInvoiceLoading(false));
    }
};

export const fetchPurchaseInvoice = (id) => async (dispatch) => {
    try {
        dispatch(setPurchaseInvoiceLoading(true));
        const response = await purchaseinvoiceService.getOne(id);
        dispatch(setPurchaseInvoiceCurrent(response.data));

        return response.data;
    } catch (error) {
        dispatch(setPurchaseInvoiceError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setPurchaseInvoiceLoading(false));
    }
};

export const createPurchaseInvoice = (data) => async (dispatch) => {
    try {
        const response = await purchaseinvoiceService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updatePurchaseInvoice = (id, data) => async (dispatch) => {
    try {
        const response = await purchaseinvoiceService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deletePurchaseInvoice = (id) => async (dispatch) => {
    try {
        dispatch(setPurchaseInvoiceLoading(true));
        const response = await purchaseinvoiceService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setPurchaseInvoiceError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setPurchaseInvoiceLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setPurchaseInvoicePagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setPurchaseInvoiceSort(sort));
};

export const searchPurchaseinvoices = (query) => (dispatch, getState) => {
    const currentFilters = getState().purchaseinvoice.filters;
    dispatch(setPurchaseInvoiceFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setPurchaseInvoiceDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changePurchaseInvoiceFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addPurchaseInvoiceFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updatePurchaseInvoiceFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updatePurchaseInvoiceFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removePurchaseInvoiceFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setPurchaseInvoiceFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().purchaseinvoice.settings;
            dispatch(setPurchaseInvoiceSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setPurchaseInvoiceFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setPurchaseInvoiceFilterLoading(false));
        dispatch(setPurchaseInvoiceError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setPurchaseInvoiceSelected(selectedRows));
    };
};

export const clearCurrentPurchaseInvoice = () => {
    return (dispatch) => {
        dispatch(clearPurchaseInvoiceCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetPurchaseInvoiceFiltersAndPagination());
};